import * as React from 'react';
import { useField } from 'formik';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Input, type InputProps } from '../primitives';
import { FormField } from './FormField';

export interface FormInputProps extends Omit<InputProps, 'error' | 'name'> {
  name: string;
  label?: React.ReactNode;
  help?: React.ReactNode;
  /** Content rendered on the right of the label row. */
  labelRight?: React.ReactNode;
  /** Wrapper className (the input itself uses `className`). */
  fieldClassName?: string;
  /** Suppress the error text below the field (error styling still applies). */
  hideError?: boolean;
  /**
   * Async-uniqueness affordance (ported from the legacy FormikInput): shows a
   * green check when the value is valid and differs from `validStatusIgnore`,
   * a spinner while `loading`, and surfaces validation errors without
   * waiting for touched.
   */
  displayValidStatus?: boolean;
  validStatusIgnore?: string;
  loading?: boolean;
  /**
   * Rendered beside the input, on the same row — a status the field's value
   * produces rather than a decoration inside it. `suffix` sits inside the
   * border and is sized for an icon; the handle field's "Available" badge is
   * a word, and the design puts it outside.
   */
  trailing?: React.ReactNode;
  /**
   * Show the value as an amount of money — "89.00", not "89".
   *
   * A number input holds the bare number, so a price the seller typed as 89
   * sat in the field reading "89" while every other place it appears says
   * $89.00. The field now formats to two decimals whenever it is not being
   * typed into, and hands back the raw number while it is, so editing stays
   * ordinary. Formik still holds a number.
   */
  money?: boolean;
  /**
   * The Formik value this `money` field holds is INTEGER CENTS, not dollars.
   *
   * The field still shows and accepts dollars — a seller types 19.99 — but
   * what it stores is 1999. Used where the value goes straight to a GraphQL
   * money field, which carries cents (MPPRO-45): `variant.price`. Only has
   * an effect together with `money`.
   *
   * Opt-in rather than the default for `money`, because the dollar-valued
   * money fields on this form are not all migrated — a delivery profile's
   * `rate` and a product's `domesticRate` are still dollars, and silently
   * multiplying those by 100 is the defect this prop exists to avoid.
   */
  cents?: boolean;
}

/** "89" / 89 → "89.00". Blank stays blank rather than becoming "0.00". */
const asMoney = (value: unknown): string => {
  if (value === '' || value === null || value === undefined) return '';
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? n.toFixed(2) : String(value);
};

/** 1999 → "19.99", for display. Blank stays blank. */
const centsAsMoney = (value: unknown): string => {
  if (value === '' || value === null || value === undefined) return '';
  const n = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(n) ? asMoney(n / 100) : String(value);
};

/**
 * "19.99" → 1999, for storage. Rounds rather than truncating: 19.99 * 100 is
 * 1998.9999999999998 in binary floating point, so a bare multiply would store
 * a cent less than the seller typed.
 *
 * A value that is not yet a number — mid-typing "19." or "-" — is handed back
 * as the raw string, exactly as the dollars path does, so Formik's own
 * validation reports it rather than this converter inventing a number.
 */
const moneyAsCents = (next: string): number | string => {
  if (next.trim() === '') return '';
  const n = Number.parseFloat(next);
  return Number.isFinite(n) ? Math.round(n * 100) : next;
};

/** Formik-bound text input built on the design-system `Input`. */
export const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  help,
  labelRight,
  required,
  id,
  fieldClassName,
  value,
  onChange,
  hideError,
  displayValidStatus,
  validStatusIgnore,
  loading,
  trailing,
  money,
  cents,
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const inputId = id ?? name;
  // While the field has focus it shows exactly what is being typed; the moment
  // it loses focus the draft is dropped and the formatted value takes over.
  const [draft, setDraft] = React.useState<string | null>(null);

  let error = meta.touched ? meta.error : undefined;
  let suffix = props.suffix;
  if (displayValidStatus) {
    if (!meta.error && validStatusIgnore?.trim() !== String(field.value ?? '').trim()) {
      suffix = <CheckCircle2 className="h-4 w-4 text-green-500" />;
      error = undefined;
    } else if (loading) {
      suffix = <Loader2 className="h-4 w-4 animate-spin" />;
      error = undefined;
    } else if (meta.error) {
      error = meta.error;
    }
  }

  return (
    <FormField
      label={label}
      htmlFor={inputId}
      required={required}
      error={hideError ? undefined : error}
      help={help}
      labelRight={labelRight}
      className={fieldClassName}
    >
      {/* Explicit value/onChange props take over the field wiring — used by
          controlled fields like the computed-stock quantity input. */}
      <div className={trailing ? 'flex items-center gap-2' : undefined}>
        <Input
          id={inputId}
          required={required}
          {...props}
          {...field}
          suffix={suffix}
          error={!!error}
          {...(money
            ? {
                // A number input cannot be made to sit at "89.00" — the browser
                // normalises it away — so money fields are text that only
                // accepts decimal input.
                type: 'text',
                inputMode: 'decimal' as const,
                step: undefined,
                min: undefined,
                value:
                  draft ??
                  (cents
                    ? centsAsMoney(value ?? field.value)
                    : asMoney(value ?? field.value)),
                // Deliberately no onFocus. Swapping the text as the field gains
                // focus re-renders the input in the middle of the browser's
                // own select-then-insert, which collapses the selection — so
                // selecting all and typing appended instead of replacing, and
                // "34.00" became "3429.99". The formatted value simply stays
                // until it is edited.
                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                  // The product editor's `<Form onChange={handleChange}>` catches
                  // every change that bubbles, and Formik's handleChange parses
                  // `type="number"` inputs to a number but stores anything else
                  // as the raw string. Making this field text therefore handed
                  // Formik "29.99", and because the form-level handler runs after
                  // this one it won, so the price went to a `Float!` argument as
                  // a string and the save was rejected. This field sets its own
                  // value; the catch-all must not also set it.
                  e.stopPropagation();
                  const next = e.target.value;
                  setDraft(next);
                  if (cents) {
                    helpers.setValue(moneyAsCents(next));
                    return;
                  }
                  const n = Number.parseFloat(next);
                  helpers.setValue(next.trim() === '' ? '' : Number.isFinite(n) ? n : next);
                },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  setDraft(null);
                  field.onBlur(e);
                },
              }
            : {
                value: value ?? field.value ?? '',
                onChange: onChange ?? field.onChange,
              })}
        />
        {trailing}
      </div>
    </FormField>
  );
};
